import { Divider } from "@mui/material"
import type { FC } from "react"

import { SpecValue } from "./SpecValue"

import { NoValuePlaceholder } from "@/components/common/NoValuePlaceholder"
import type { Product } from "@/graphql/codegen/graphql"
import { getProductShelfLifeTimeFormat } from "@/screens/Products/components/Product/utils"
import { formatNumber } from "@/utils/number"
import { getProductPackagingTypeLabel } from "@/utils/product"
import { booleanToYesNoString } from "@/utils/utils"

interface ProductInventoryHeaderSpecsProps {
  product: Product
}

export const ProductInventoryHeaderSpecs: FC<ProductInventoryHeaderSpecsProps> = ({ product }) => {
  const uomName = product.packageUsWeightUomDetails?.name ?? ""
  const shelfLifeDays = product.shelfLifeDays ?? 0
  const { divisible: shelfLifeUnitDivisor, format: shelfLifeUnitLabel } = getProductShelfLifeTimeFormat(shelfLifeDays)
  const shelfLife = Number((shelfLifeDays / shelfLifeUnitDivisor).toFixed(1))

  return (
    <div className='flex flex-[3_1_0] flex-col justify-between gap-3'>
      <div className='flex justify-between gap-6'>
        <SpecValue
          label='Shelf Life'
          value={product.shelfLifeDays ? `${shelfLife} ${shelfLifeUnitLabel}` : <NoValuePlaceholder />}
        />
        <SpecValue
          label='Std. Packaging'
          value={
            product.packageUsWeight ? (
              `${formatNumber(product.packageUsWeight)} ${uomName} ${getProductPackagingTypeLabel(product.packagingType)}`
            ) : (
              <NoValuePlaceholder />
            )
          }
        />
        <SpecValue
          label='UoM per Pallet'
          value={product.palletUsWeight ? `${formatNumber(product.palletUsWeight)} ${uomName}` : <NoValuePlaceholder />}
        />
        <SpecValue
          label='Specific Gravity'
          value={product.specificGravity ? `${product.specificGravity}` : <NoValuePlaceholder />}
        />
      </div>

      <Divider />

      <div className='flex justify-between gap-6'>
        <SpecValue
          label='Trade Compliance'
          value={
            <div className='flex gap-x-4'>
              <div>USMCA {product.usmcaClass ? booleanToYesNoString(product.usmcaClass) : <NoValuePlaceholder />}</div>
              <div>COO {product.countryOrigin?.country?.name ?? <NoValuePlaceholder />}</div>
            </div>
          }
        />
        <SpecValue
          label='HTS / FRT'
          value={
            <div className='flex gap-x-4'>
              <div>HTS {product.harmonizedTariffSchedule || <NoValuePlaceholder />}</div>
              <div>
                FRT {product.freightItem ? `${product.freightItem}.${product.freightClass}` : <NoValuePlaceholder />}
              </div>
            </div>
          }
        />
        <SpecValue label='Hazard Status' value={<NoValuePlaceholder />} />
      </div>
    </div>
  )
}
